import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/api';
import HandoverReviewModal from './HandoverReviewModal';
import {
  Package, Search, Shield, Wrench, FileCheck, ChevronDown, ChevronRight,
  Loader2, Sparkles, CheckCircle2, XCircle, AlertTriangle, ExternalLink,
  Upload, Trash2, RefreshCw, Eye, EyeOff, Link2, Cpu, ClipboardCheck,
  ArrowRightLeft, Building2, MapPin, Hash, Download
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  active: { color: 'text-emerald-700 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-950/30', border: 'border-emerald-300 dark:border-emerald-900', label: 'Active' },
  ve_removed: { color: 'text-red-700 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-950/30', border: 'border-red-300 dark:border-red-900', label: 'VE Removed' },
  substituted: { color: 'text-amber-700 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-950/30', border: 'border-amber-300 dark:border-amber-900', label: 'Substituted' },
  pending_review: { color: 'text-blue-700 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-950/30', border: 'border-blue-300 dark:border-blue-900', label: 'Review' },
};

const ATTR_LABELS = {
  btu: 'BTU', tons: 'Tons', hp: 'HP', cfm: 'CFM', gpm: 'GPM',
  voltage: 'Voltage', amps: 'Amps', watts: 'Watts', seer: 'SEER', cop: 'COP',
  size: 'Size', weight: 'Weight', diameter: 'Dia.', length: 'Length',
  area_sqft: 'Area (sf)', species: 'Species', gallons: 'Gallons',
  capacity: 'Capacity', phase: 'Phase', frequency: 'Freq.',
  color: 'Color', finish: 'Finish', material: 'Material',
  flush_type: 'Flush', ada: 'ADA', height: 'Height', width: 'Width',
  depth: 'Depth', noise_level: 'Noise', efficiency: 'Efficiency',
};

function WarrantySection({ warranty }) {
  if (!warranty || !warranty.warranty_components?.length) return null;
  return (
    <div className="space-y-2" data-testid="asset-warranty-info">
      <div className="flex items-center gap-2 text-xs font-heading uppercase tracking-wider text-primary">
        <Shield className="w-3.5 h-3.5" />
        Warranty Info
        {warranty.source_type && (
          <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded border ${
            warranty.source_type === 'OEM' ? 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-300 dark:border-emerald-900/40 text-emerald-700 dark:text-emerald-500' :
            'bg-orange-950/40 border-primary/30/40 text-primary'
          }`}>
            {warranty.source_type}
          </span>
        )}
        {warranty.verified_urls_count > 0 && (
          <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-300 dark:border-emerald-900/40 text-emerald-700 dark:text-emerald-500">
            {warranty.verified_urls_count} verified link{warranty.verified_urls_count > 1 ? 's' : ''}
          </span>
        )}
      </div>
      {warranty.needs_manufacturer_data && (
        <div className="flex items-center gap-2 px-2 py-1.5 bg-amber-100 dark:bg-amber-950/20 border border-amber-300 dark:border-amber-900/30 rounded-sm">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
          <p className="text-[10px] text-amber-700 dark:text-amber-400">Manufacturer data needed — add make/model from submittals for OEM-specific warranty terms</p>
        </div>
      )}
      {warranty.warranty_summary && (
        <p className="text-xs text-muted-foreground">{warranty.warranty_summary}</p>
      )}
      <div className="space-y-1">
        {warranty.warranty_components.map((c, i) => (
          <div key={i} className="flex items-center gap-3 text-xs px-2 py-1.5 bg-secondary/30 rounded-sm border border-border/50">
            <span className="text-foreground/80 font-medium flex-1">{c.component}</span>
            <span className="font-mono text-emerald-600 dark:text-emerald-400">{c.parts_years}yr parts</span>
            <span className="font-mono text-blue-600 dark:text-blue-400">{c.labor_years}yr labor</span>
            {c.requires_registration && <span className="text-[9px] text-amber-500">REG</span>}
          </div>
        ))}
      </div>
      {warranty.warranty_tips?.length > 0 && (
        <div className="text-[10px] text-muted-foreground space-y-0.5">
          {warranty.warranty_tips.map((t, i) => <p key={i}>* {t}</p>)}
        </div>
      )}
      <div className="flex gap-2 flex-wrap">
        {warranty.product_page_url && (
          <a href={warranty.product_page_url} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1 text-[10px] text-emerald-500 hover:text-emerald-400">
            <ExternalLink className="w-3 h-3" /> Product Page
          </a>
        )}
        {warranty.registration_url && (
          <a href={warranty.registration_url} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1 text-[10px] text-blue-500 hover:text-blue-400">
            <ExternalLink className="w-3 h-3" /> Register Warranty
          </a>
        )}
        {warranty.spec_sheet_url && (
          <a href={warranty.spec_sheet_url} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1 text-[10px] text-cyan-500 hover:text-cyan-400">
            <ExternalLink className="w-3 h-3" /> Spec Sheet
          </a>
        )}
        {warranty.lookup_url && !warranty.registration_url && (
          <a href={warranty.lookup_url} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1 text-[10px] text-primary hover:text-primary">
            <ExternalLink className="w-3 h-3" /> Warranty Lookup
          </a>
        )}
      </div>
    </div>
  );
}

function OmSection({ om }) {
  const [showAll, setShowAll] = useState(false);
  if (!om || (!om.tasks?.length && !om.om_manual_links?.length)) return null;
  const visibleTasks = showAll ? om.tasks : (om.tasks || []).slice(0, 3);

  return (
    <div className="space-y-2" data-testid="asset-om-info">
      <div className="flex items-center gap-2 text-xs font-heading uppercase tracking-wider text-blue-700 dark:text-blue-400">
        <Wrench className="w-3.5 h-3.5" />
        O&M / Preventive Maintenance
        {om.source_type && (
          <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded border ${
            om.source_type === 'OEM' ? 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-300 dark:border-emerald-900/40 text-emerald-700 dark:text-emerald-500' :
            'bg-blue-50 dark:bg-blue-950/40 border-blue-300 dark:border-blue-900/40 text-blue-700 dark:text-blue-500'
          }`}>
            {om.source_type}
          </span>
        )}
        {om.verified_urls_count > 0 && (
          <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-300 dark:border-emerald-900/40 text-emerald-700 dark:text-emerald-500">
            {om.verified_urls_count} verified link{om.verified_urls_count > 1 ? 's' : ''}
          </span>
        )}
      </div>
      {om.needs_manufacturer_data && (
        <div className="flex items-center gap-2 px-2 py-1.5 bg-amber-100 dark:bg-amber-950/20 border border-amber-300 dark:border-amber-900/30 rounded-sm">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
          <p className="text-[10px] text-amber-700 dark:text-amber-400">Manufacturer data needed — add make/model from submittals for OEM-specific manuals</p>
        </div>
      )}
      {om.maintenance_notes && (
        <p className="text-xs text-muted-foreground">{om.maintenance_notes}</p>
      )}
      {/* O&M Manual Links */}
      {om.om_manual_links?.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {om.om_manual_links.map((link, i) => (
            <a key={i} href={link.url} target="_blank" rel="noopener noreferrer"
              className={`flex items-center gap-1 px-2 py-1 text-[10px] rounded-sm border transition-colors ${
                (link.source === 'web_search' || link.source === 'serper_google') ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-300 dark:border-emerald-900/40 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-950/50' :
                'bg-blue-50 dark:bg-blue-950/30 border-blue-300 dark:border-blue-900/40 text-blue-700 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-950/50'
              }`}>
              <Link2 className="w-3 h-3" />
              {link.title}
              {(link.source === 'web_search' || link.source === 'serper_google') && <CheckCircle2 className="w-2.5 h-2.5 text-emerald-500" />}
            </a>
          ))}
        </div>
      )}
      {/* PM Tasks */}
      {visibleTasks?.length > 0 && (
        <div className="space-y-1">
          {visibleTasks.map((t, i) => (
            <div key={i} className="flex items-center gap-3 text-xs px-2 py-1.5 bg-secondary/30 rounded-sm border border-border/50">
              <span className="text-foreground/80 flex-1">{t.task}</span>
              <span className="font-mono text-muted-foreground text-[10px]">{t.frequency}</span>
              <span className={`text-[9px] font-mono px-1 rounded ${
                t.priority === 'High' || t.priority === 'Critical' ? 'bg-red-100 dark:bg-red-950/40 text-red-700 dark:text-red-400' :
                t.priority === 'Medium' ? 'bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400' : 'bg-secondary text-muted-foreground'
              }`}>{t.priority}</span>
              <span className="font-mono text-muted-foreground text-[10px]">{t.estimated_hours}h</span>
            </div>
          ))}
          {om.tasks?.length > 3 && (
            <button onClick={() => setShowAll(!showAll)}
              className="text-[10px] text-muted-foreground hover:text-muted-foreground">
              {showAll ? 'Show less' : `+ ${om.tasks.length - 3} more tasks`}
            </button>
          )}
        </div>
      )}
      {/* Critical Parts */}
      {om.critical_parts?.length > 0 && (
        <div className="mt-2">
          <p className="text-[10px] font-heading uppercase tracking-wider text-muted-foreground mb-1">Critical Spare Parts</p>
          <div className="flex gap-1 flex-wrap">
            {om.critical_parts.map((p, i) => (
              <span key={i} className="px-2 py-0.5 text-[10px] rounded-sm bg-muted/50 border border-border text-muted-foreground"
                title={`${p.description} | Lifespan: ${p.typical_lifespan_years}yr | $${p.estimated_cost_min}-${p.estimated_cost_max}`}>
                {p.name}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CxSection({ cxDocs, projectId, assetId, onRefresh }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', file.name.replace(/\.[^/.]+$/, ''));
      fd.append('document_type', 'other');
      await api.post(`/closeout/projects/${projectId}/assets/${assetId}/cx-docs`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onRefresh();
    } catch (err) { console.error(err); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleDelete = async (docId) => {
    try {
      await api.delete(`/closeout/projects/${projectId}/assets/${assetId}/cx-docs/${docId}`);
      onRefresh();
    } catch (err) { console.error(err); }
  };

  return (
    <div className="space-y-2" data-testid="asset-cx-docs">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-heading uppercase tracking-wider text-violet-700 dark:text-violet-400">
          <ClipboardCheck className="w-3.5 h-3.5" />
          Commissioning Documents
        </div>
        <label className="flex items-center gap-1 px-2 py-1 text-[10px] text-violet-700 dark:text-violet-400 border border-violet-300 dark:border-violet-800/40 rounded-sm cursor-pointer hover:bg-violet-50 dark:hover:bg-violet-950/30 transition-colors"
          data-testid="cx-upload-btn">
          {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
          Upload Cx Doc
          <input ref={fileRef} type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.xlsx,.docx" onChange={handleUpload} />
        </label>
      </div>
      {cxDocs?.length > 0 ? (
        <div className="space-y-1">
          {cxDocs.map((doc) => (
            <div key={doc.id} className="flex items-center gap-3 px-2 py-1.5 bg-secondary/30 rounded-sm border border-border/50 text-xs">
              <FileCheck className="w-3.5 h-3.5 text-violet-600 dark:text-violet-400 shrink-0" />
              <span className="text-foreground/80 flex-1 truncate">{doc.title}</span>
              <span className="text-[9px] font-mono text-muted-foreground">{doc.document_type?.replace('_', ' ')}</span>
              {doc.file_url && (
                <a href={doc.file_url} target="_blank" rel="noopener noreferrer"
                  className="text-violet-500 hover:text-violet-400">
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
              <button onClick={() => handleDelete(doc.id)} className="text-muted-foreground hover:text-red-400">
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[10px] text-muted-foreground">No commissioning documents uploaded yet</p>
      )}
    </div>
  );
}

function AssetCard({ asset, projectId, onRefresh, currentTab, onInlineEdit, onReassign }) {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const openOnDrawing = (p) => {
    if (!asset.source_drawing_job_id) return;
    const params = new URLSearchParams();
    if (p?.sheet_number) params.set('sheet', p.sheet_number);
    if (asset.source_tag) params.set('tag', asset.source_tag);
    navigate(`/jobs/${asset.source_drawing_job_id}/review?${params.toString()}`);
  };
  const [finding, setFinding] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editFields, setEditFields] = useState({});
  const st = statusConfig[asset.status] || statusConfig.active;

  const handleFindDocs = async () => {
    setFinding(true);
    try {
      await api.post(`/closeout/projects/${projectId}/assets/${asset.id}/find-docs`);
      onRefresh();
    } catch (err) { console.error(err); }
    finally { setFinding(false); }
  };

  const startEdit = () => {
    setEditing(true);
    setEditFields({
      make: asset.make || '',
      model: asset.model || '',
      location: asset.location || '',
      equipment_type: asset.equipment_type || '',
      notes: asset.notes || '',
      attributes: { ...(asset.attributes || {}) },
    });
  };

  const saveEdit = () => {
    const changes = {};
    if (editFields.make !== (asset.make || '')) changes.make = editFields.make || null;
    if (editFields.model !== (asset.model || '')) changes.model = editFields.model || null;
    if (editFields.location !== (asset.location || '')) changes.location = editFields.location || null;
    if (editFields.equipment_type !== (asset.equipment_type || '')) changes.equipment_type = editFields.equipment_type;
    if (editFields.notes !== (asset.notes || '')) changes.notes = editFields.notes;
    // Check attributes changes
    const origAttrs = asset.attributes || {};
    const newAttrs = editFields.attributes || {};
    const attrsChanged = JSON.stringify(origAttrs) !== JSON.stringify(newAttrs);
    if (attrsChanged) {
      // Filter out empty values
      const cleaned = {};
      for (const [k, v] of Object.entries(newAttrs)) {
        if (v && String(v).trim()) cleaned[k] = String(v).trim();
      }
      changes.attributes = cleaned;
    }
    if (Object.keys(changes).length > 0) {
      onInlineEdit(asset.id, changes);
    }
    setEditing(false);
  };

  const isEditable = currentTab === 'design_chase' || currentTab === 'chaser';

  const hasWarranty = !!asset.warranty_info?.warranty_components?.length;
  const hasOm = !!asset.om_info?.tasks?.length;
  const hasCx = !!asset.cx_documents?.length;
  const equipName = `${asset.make || ''} ${asset.model || ''} ${asset.equipment_type}`.trim();
  const attrs = asset.attributes || {};
  const attrEntries = Object.entries(attrs).filter(([, v]) => v);

  // Inventory class badge config
  const invClassConfig = {
    equipment: { label: 'Equipment', color: 'text-emerald-700 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-950/30', border: 'border-emerald-300 dark:border-emerald-800/30' },
    design_chase: { label: 'Design Chase', color: 'text-amber-700 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-950/30', border: 'border-amber-300 dark:border-amber-800/30' },
    landscape: { label: 'Landscape', color: 'text-green-700 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-950/30', border: 'border-green-300 dark:border-green-800/30' },
    site_civil: { label: 'Site & Civil', color: 'text-slate-700 dark:text-slate-400', bg: 'bg-slate-50 dark:bg-slate-950/30', border: 'border-slate-300 dark:border-slate-800/30' },
    architectural_finishes: { label: 'Finishes', color: 'text-violet-700 dark:text-violet-400', bg: 'bg-violet-50 dark:bg-violet-950/30', border: 'border-violet-300 dark:border-violet-800/30' },
    structural: { label: 'Structural', color: 'text-orange-700 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-950/30', border: 'border-orange-300 dark:border-orange-800/30' },
    chaser: { label: 'Chaser', color: 'text-red-700 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-950/30', border: 'border-red-300 dark:border-red-800/30' },
  };
  const invClass = invClassConfig[asset.inventory_class] || invClassConfig.chaser;

  return (
    <div className={`border rounded-sm transition-colors ${st.border} ${asset.status === 'active' ? 'bg-card' : st.bg}`}
      data-testid={`asset-card-${asset.id}`}>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
        <Cpu className={`w-4 h-4 ${st.color} shrink-0`} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground truncate">{equipName}</p>
          <div className="flex items-center gap-3 mt-0.5 text-[10px] text-muted-foreground flex-wrap">
            {asset.csi_number && <span className="font-mono">{asset.csi_number}</span>}
            {asset.uniformat?.code && (
              <span className="font-mono px-1 py-0.5 rounded bg-teal-50 dark:bg-teal-950/30 text-teal-700 dark:text-teal-400 border border-teal-200 dark:border-teal-800/30" data-testid={`uniformat-${asset.id}`}>
                UF {asset.uniformat.code}
              </span>
            )}
            {asset.location && (
              <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{asset.location}</span>
            )}
            {!asset.location && (currentTab === 'design_chase' || currentTab === 'equipment') && (
              <span className="flex items-center gap-0.5 text-amber-500"><MapPin className="w-3 h-3" />No location</span>
            )}
            {!asset.make && (currentTab === 'design_chase') && (
              <span className="text-amber-500">No make/model</span>
            )}
            {asset.is_spec_mandatory && currentTab === 'design_chase' && (
              <span className="text-[9px] font-mono font-bold px-1.5 py-0.5 rounded-sm bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 border border-amber-300 dark:border-amber-700/50"
                data-testid={`spec-required-${asset.id}`}>
                SPEC REQUIRED
              </span>
            )}
            {asset.split_from_group && asset.original_group_qty && (
              <span className="text-[9px] font-mono px-1 py-0.5 rounded bg-indigo-50 dark:bg-indigo-950/30 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800/30">
                Group of {asset.original_group_qty}
              </span>
            )}
            {attrEntries.length > 0 && (
              <span className="flex items-center gap-1 text-[9px] text-cyan-600 dark:text-cyan-400">
                {attrEntries.slice(0, 3).map(([k, v]) => (
                  <span key={k} className="font-mono bg-cyan-50 dark:bg-cyan-950/20 px-1 rounded border border-cyan-200 dark:border-cyan-800/30">
                    {ATTR_LABELS[k] || k}: {v}
                  </span>
                ))}
                {attrEntries.length > 3 && <span>+{attrEntries.length - 3}</span>}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          {asset.verified_by_user && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" title="User verified" />}
          {hasWarranty && <Shield className="w-3.5 h-3.5 text-primary" title="Warranty info found" />}
          {hasOm && <Wrench className="w-3.5 h-3.5 text-blue-500" title="O&M info found" />}
          {hasCx && <ClipboardCheck className="w-3.5 h-3.5 text-violet-500" title="Cx docs attached" />}
        </div>

        <span className={`text-[8px] font-heading uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${invClass.bg} ${invClass.color} border ${invClass.border}`}>
          {invClass.label}
        </span>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t border-border/50 space-y-4">
          {/* Action buttons row */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* Inline Edit for Design Chase / Chaser */}
            {isEditable && !editing && (
              <button onClick={(e) => { e.stopPropagation(); startEdit(); }}
                data-testid={`edit-asset-${asset.id}`}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-amber-600 dark:text-amber-400 border border-amber-300 dark:border-amber-800/40 rounded-sm hover:bg-amber-50 dark:hover:bg-amber-950/30 transition-colors">
                <Wrench className="w-3 h-3" /> Edit Details
              </button>
            )}

            {/* Mark as Verified */}
            <button onClick={async (e) => {
                e.stopPropagation();
                try {
                  await api.post(`/closeout/projects/${projectId}/assets/${asset.id}/verify`);
                  onRefresh();
                } catch (err) { console.error(err); }
              }}
              data-testid={`verify-asset-${asset.id}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-sm transition-colors border ${
                asset.verified_by_user
                  ? 'text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-700/50 bg-emerald-50 dark:bg-emerald-950/20'
                  : 'text-muted-foreground border-border hover:bg-muted/30'
              }`}
            >
              <CheckCircle2 className="w-3 h-3" />
              {asset.verified_by_user ? 'Verified — Locked' : 'Mark Verified'}
            </button>
          </div>

          {/* Verified banner */}
          {asset.verified_by_user && (
            <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800/30 rounded-sm text-[10px]"
              data-testid={`verified-banner-${asset.id}`}>
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
              <span className="text-emerald-700 dark:text-emerald-400 font-medium">Verified — this asset is locked from re-extraction changes</span>
              {asset.verified_by && <span className="text-muted-foreground ml-auto">by {asset.verified_by}</span>}
            </div>
          )}

          {editing && (
            <div className="space-y-2 p-3 bg-muted/30 border border-border rounded-sm" data-testid={`edit-form-${asset.id}`}>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase tracking-wider">Equipment Type</label>
                  <input value={editFields.equipment_type} onChange={(e) => setEditFields(f => ({ ...f, equipment_type: e.target.value }))}
                    className="w-full mt-0.5 px-2 py-1.5 text-xs bg-background border border-border rounded-sm focus:outline-none focus:border-primary"
                    data-testid={`edit-type-${asset.id}`} />
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase tracking-wider">Location</label>
                  <input value={editFields.location} onChange={(e) => setEditFields(f => ({ ...f, location: e.target.value }))}
                    className="w-full mt-0.5 px-2 py-1.5 text-xs bg-background border border-border rounded-sm focus:outline-none focus:border-primary"
                    data-testid={`edit-location-${asset.id}`} />
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase tracking-wider">Manufacturer</label>
                  <input value={editFields.make} onChange={(e) => setEditFields(f => ({ ...f, make: e.target.value }))}
                    className="w-full mt-0.5 px-2 py-1.5 text-xs bg-background border border-border rounded-sm focus:outline-none focus:border-primary"
                    data-testid={`edit-make-${asset.id}`} />
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase tracking-wider">Model</label>
                  <input value={editFields.model} onChange={(e) => setEditFields(f => ({ ...f, model: e.target.value }))}
                    className="w-full mt-0.5 px-2 py-1.5 text-xs bg-background border border-border rounded-sm focus:outline-none focus:border-primary"
                    data-testid={`edit-model-${asset.id}`} />
                </div>
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground uppercase tracking-wider">Notes</label>
                <input value={editFields.notes} onChange={(e) => setEditFields(f => ({ ...f, notes: e.target.value }))}
                  className="w-full mt-0.5 px-2 py-1.5 text-xs bg-background border border-border rounded-sm focus:outline-none focus:border-primary"
                  placeholder="e.g., RFI pending for spec clarification"
                  data-testid={`edit-notes-${asset.id}`} />
              </div>
              {/* Performance Spec Attributes */}
              <div>
                <label className="text-[10px] text-muted-foreground uppercase tracking-wider">Performance Specs</label>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5 mt-1">
                  {['btu', 'tons', 'hp', 'cfm', 'gpm', 'voltage', 'amps', 'watts'].map(key => (
                    <div key={key} className="flex items-center gap-1">
                      <span className="text-[8px] text-cyan-600 dark:text-cyan-500 uppercase w-10 shrink-0">{ATTR_LABELS[key] || key}</span>
                      <input
                        value={(editFields.attributes || {})[key] || ''}
                        onChange={(e) => setEditFields(f => ({
                          ...f,
                          attributes: { ...(f.attributes || {}), [key]: e.target.value }
                        }))}
                        className="w-full px-1.5 py-1 text-[10px] font-mono bg-background border border-border rounded-sm focus:outline-none focus:border-cyan-500"
                        placeholder="—"
                        data-testid={`edit-attr-${key}-${asset.id}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <button onClick={saveEdit} data-testid={`save-edit-${asset.id}`}
                  className="px-3 py-1.5 text-xs bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm transition-colors">
                  Save
                </button>
                <button onClick={() => setEditing(false)}
                  className="px-3 py-1.5 text-xs text-muted-foreground border border-border rounded-sm hover:border-muted-foreground transition-colors">
                  Cancel
                </button>
                {currentTab === 'design_chase' && editFields.make && editFields.model && editFields.location && (
                  <span className="text-[10px] text-emerald-500 ml-2">
                    Auto-promotes to Equipment on save
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Reclassify — available on ALL tabs */}
          {!editing && (
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Move to:</span>
              {['equipment', 'design_chase', 'landscape', 'site_civil', 'architectural_finishes', 'structural', 'chaser']
                .filter(cls => cls !== currentTab)
                .map(cls => (
                <button key={cls} onClick={() => onReassign(asset.id, cls)}
                  data-testid={`reassign-${asset.id}-${cls}`}
                  className="px-2 py-1 text-[9px] font-heading uppercase tracking-wider rounded-sm border border-border text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors">
                  {cls.replace(/_/g, ' ')}
                </button>
              ))}
            </div>
          )}

          {/* Action row */}
          {asset.status === 'active' && !editing && (
            <div className="flex items-center gap-2">
              {(!hasWarranty || !hasOm) && (
                <button onClick={handleFindDocs} disabled={finding}
                  data-testid={`find-docs-${asset.id}`}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-sm transition-colors disabled:opacity-50">
                  {finding ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
                  {finding ? 'Searching...' : 'Find Warranty & O&M'}
                </button>
              )}
              {(hasWarranty && hasOm) && (
                <button onClick={handleFindDocs} disabled={finding}
                  className="flex items-center gap-1.5 px-2 py-1 text-[10px] text-muted-foreground hover:text-foreground/80 border border-border rounded-sm transition-colors disabled:opacity-50">
                  {finding ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                  Refresh
                </button>
              )}
            </div>
          )}

          {/* Classification notes */}
          {asset.classification_notes && (
            <p className="text-[10px] text-muted-foreground italic">AI: {asset.classification_notes}</p>
          )}

          {/* Source info */}
          {asset.source_description && (
            <p className="text-[10px] text-muted-foreground">Source: {asset.source_description}</p>
          )}

          {/* Attributes detail */}
          {attrEntries.length > 0 && (
            <div>
              <p className="text-[9px] text-muted-foreground uppercase tracking-wider mb-1.5">Performance Specs</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1.5">
                {attrEntries.map(([k, v]) => {
                  const label = ATTR_LABELS[k] || k.replace(/_/g, ' ');
                  return (
                    <div key={k} className="flex items-center gap-1.5 px-2 py-1 rounded-sm bg-cyan-50 dark:bg-cyan-950/20 border border-cyan-200 dark:border-cyan-800/30">
                      <span className="text-[9px] text-cyan-600 dark:text-cyan-500 uppercase tracking-wider shrink-0">{label}</span>
                      <span className="text-[10px] font-mono font-medium text-cyan-800 dark:text-cyan-300 truncate">{v}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Locations — placement list (room + sheet), expandable per group */}
          {asset.placements?.length > 0 && (
            <div data-testid={`asset-locations-${asset.id}`}>
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-[9px] text-muted-foreground uppercase tracking-wider">
                  Locations · {asset.quantity} unit{asset.quantity === 1 ? '' : 's'}
                  {asset.quantity_source === 'assumed' && <span className="text-amber-500 normal-case"> (qty assumed)</span>}
                  {' · '}{asset.placements.length} drawing mark{asset.placements.length === 1 ? '' : 's'}
                </p>
                {asset.source_drawing_job_id && (
                  <button onClick={() => openOnDrawing(asset.placements[0])}
                    data-testid={`view-all-on-drawing-${asset.id}`}
                    className="flex items-center gap-1 text-[10px] text-primary hover:underline">
                    <MapPin className="w-3 h-3" /> View on drawing
                  </button>
                )}
              </div>
              <div className="space-y-1 max-h-48 overflow-auto">
                {asset.placements.map((p, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 px-2 py-1 rounded-sm bg-muted/30 border border-border/50 text-[10px]">
                    <span className="flex items-center gap-1.5 min-w-0">
                      <MapPin className="w-3 h-3 text-emerald-500 shrink-0" />
                      <span className="font-mono shrink-0">{p.sheet_number}</span>
                      <span className="text-muted-foreground truncate">{p.room_name || '—'}{p.room_number ? ` ${p.room_number}` : ''}</span>
                      {p.inherited && <span className="text-[8px] text-amber-500 shrink-0" title="Room inherited from another sheet">inherited</span>}
                    </span>
                    {asset.source_drawing_job_id && (
                      <button onClick={() => openOnDrawing(p)}
                        data-testid={`view-on-drawing-${asset.id}-${i}`}
                        className="flex items-center gap-0.5 text-primary hover:underline shrink-0">
                        View <ExternalLink className="w-2.5 h-2.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <WarrantySection warranty={asset.warranty_info} />
          <OmSection om={asset.om_info} />
          <CxSection
            cxDocs={asset.cx_documents}
            projectId={projectId}
            assetId={asset.id}
            onRefresh={onRefresh}
          />
        </div>
      )}
    </div>
  );
}


export default function AssetPanel({ projectId }) {
  const [assets, setAssets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [inventoryCounts, setInventoryCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [extracting, setExtracting] = useState(false);
  const [classifying, setClassifying] = useState(false);
  const [findingAll, setFindingAll] = useState(false);
  const [filter, setFilter] = useState('equipment');
  const [extractProgress, setExtractProgress] = useState(null);
  const [docFinderProgress, setDocFinderProgress] = useState(null);
  const esRef = useRef(null);
  const regFileRef = useRef(null);
  const [buildingReg, setBuildingReg] = useState(false);
  const [buildMsg, setBuildMsg] = useState(null);
  const [showBuildReview, setShowBuildReview] = useState(false);

  const handleBuildFromDrawings = async (file) => {
    if (!file) return;
    setBuildingReg(true);
    setBuildMsg('Uploading drawing set…');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/closeout/projects/${projectId}/build-register/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const buildId = res.data.build_id;
      const poll = setInterval(async () => {
        try {
          const s = await api.get(`/closeout/projects/${projectId}/build-register/status/${buildId}`);
          setBuildMsg(s.data.message);
          if (s.data.stage === 'complete' || s.data.stage === 'error') {
            clearInterval(poll);
            setBuildingReg(false);
            if (s.data.stage === 'complete') { toast.success(s.data.message); fetchData(); setShowBuildReview(true); }
            else { toast.error(s.data.message); }
            setTimeout(() => setBuildMsg(null), 6000);
          }
        } catch (e) { clearInterval(poll); setBuildingReg(false); }
      }, 2500);
    } catch (err) {
      setBuildingReg(false);
      toast.error(err?.response?.data?.detail || 'Failed to start register build');
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [aRes, sRes, cRes] = await Promise.all([
        api.get(`/closeout/projects/${projectId}/assets?status_filter=${filter}`),
        api.get(`/closeout/projects/${projectId}/asset-summary`),
        api.get(`/closeout/projects/${projectId}/inventory-counts`),
      ]);
      setAssets(aRes.data);
      setSummary(sRes.data);
      setInventoryCounts(cRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [projectId, filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // SSE listener for extraction/finder progress
  useEffect(() => {
    if (!extracting && !findingAll && !classifying) return;
    const token = localStorage.getItem('fmiq_token');
    if (!token) return;

    const url = `${API_URL}/closeout/projects/${projectId}/events/stream?token=${encodeURIComponent(token)}`;
    let es;
    try { es = new EventSource(url); } catch { return; }
    esRef.current = es;

    es.addEventListener('asset_extraction_progress', (e) => {
      try { setExtractProgress(JSON.parse(e.data)); } catch {}
    });
    es.addEventListener('asset_extraction_complete', (e) => {
      try {
        const data = JSON.parse(e.data);
        setExtractProgress(data);
        if (data.status === 'done' || data.status === 'error') {
          setExtracting(false);
          setClassifying(false);
          fetchData();
        }
      } catch {}
    });
    es.addEventListener('doc_finder_progress', (e) => {
      try { setDocFinderProgress(JSON.parse(e.data)); } catch {}
    });
    es.addEventListener('doc_finder_complete', (e) => {
      try {
        setDocFinderProgress(null);
        setFindingAll(false);
        fetchData();
      } catch {}
    });

    es.onerror = () => {};
    return () => { es.close(); esRef.current = null; };
  }, [extracting, findingAll, classifying, projectId, fetchData]);

  const handleExtract = async () => {
    setExtracting(true);
    setExtractProgress(null);
    try {
      const res = await api.post(`/closeout/projects/${projectId}/extract-assets`);
      const batchId = res.data.batch_id;

      // Start polling as fallback (SSE may drop)
      const poll = setInterval(async () => {
        try {
          const status = await api.get(`/closeout/projects/${projectId}/extraction-status/${batchId}`);
          const data = status.data;
          if (data.status && data.message) {
            setExtractProgress(data);
          }
          if (data.status === 'done' || data.status === 'error') {
            clearInterval(poll);
            if (data.status === 'done') {
              setExtracting(false);
              fetchData();
            } else {
              setExtracting(false);
            }
          }
        } catch {
          // Polling endpoint might not exist yet for old batch — ignore
        }
      }, 4000);

      // Clean up poll when extracting stops
      const cleanup = () => clearInterval(poll);
      setTimeout(() => {
        if (!extracting) cleanup();
      }, 600000); // 10 min max
    } catch (err) {
      console.error(err);
      setExtracting(false);
    }
  };

  const handleFindAll = async () => {
    setFindingAll(true);
    setDocFinderProgress(null);
    try {
      const res = await api.post(`/closeout/projects/${projectId}/find-all-asset-docs`);
      if (res.data.skipped > 0) {
        setDocFinderProgress({
          status: 'searching', current: 0, total: res.data.assets_queued,
          note: `Searching ${res.data.assets_queued} assets with make/model (${res.data.skipped} generic/material items skipped)`,
        });
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to start doc finder';
      alert(msg);
      setFindingAll(false);
    }
  };

  const handleClassify = async () => {
    setClassifying(true);
    setExtractProgress({ status: 'classifying', message: 'Starting inventory classification...' });
    try {
      await api.post(`/closeout/projects/${projectId}/classify-inventory`);
      // Poll for completion
      const poll = setInterval(async () => {
        try {
          const cRes = await api.get(`/closeout/projects/${projectId}/inventory-counts`);
          const counts = cRes.data;
          if (counts.total > 0) {
            clearInterval(poll);
            setClassifying(false);
            setExtractProgress(null);
            fetchData();
          }
        } catch {}
      }, 4000);
      setTimeout(() => clearInterval(poll), 300000);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to classify');
      setClassifying(false);
      setExtractProgress(null);
    }
  };

  const handleInlineEdit = async (assetId, updates) => {
    try {
      await api.patch(`/closeout/projects/${projectId}/assets/${assetId}/inline-edit`, updates);
      fetchData();
    } catch (err) { console.error(err); }
  };

  const handleReassign = async (assetId, targetClass) => {
    try {
      await api.post(`/closeout/projects/${projectId}/assets/${assetId}/reassign`, { target_class: targetClass });
      fetchData();
    } catch (err) { console.error(err); }
  };

  const handleExport = async (fmt = 'xlsx') => {
    try {
      const res = await api.get(`/closeout/projects/${projectId}/assets/export?fmt=${fmt}`, {
        responseType: 'blob',
      });
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `assets_export.${fmt}`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { console.error(err); }
  };

  const handleExportChaseList = async () => {
    try {
      const res = await api.get(`/closeout/projects/${projectId}/assets/export-chase-list`, {
        responseType: 'blob',
      });
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : 'Chase_List.xlsx';
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { console.error(err); }
  };

  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const chaseFileRef = useRef(null);

  const handleImportChaseResponse = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(
        `/closeout/projects/${projectId}/assets/import-chase-response`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setImportResult(res.data);
      fetchData();
    } catch (err) {
      setImportResult({ status: 'error', message: err.response?.data?.detail || 'Import failed' });
    } finally {
      setImporting(false);
      if (chaseFileRef.current) chaseFileRef.current.value = '';
    }
  };

  const [verifying, setVerifying] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  const handleBulkVerify = async () => {
    if (!window.confirm(`Mark all ${activeAssets.filter(a => a.ai_extracted && !a.verified_by_user).length} AI-extracted assets as verified?`)) return;
    setVerifying(true);
    try {
      const res = await api.post(`/closeout/projects/${projectId}/assets/bulk-verify`);
      if (res.data.verified_count > 0) fetchData();
    } catch (err) { console.error(err); }
    finally { setVerifying(false); }
  };

  const activeAssets = assets.filter(a => a.status === 'active');

  return (
    <div data-testid="asset-panel">
      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-5 gap-3 mb-5">
          {[
            { label: 'Active', count: summary.active, color: 'text-emerald-600 dark:text-emerald-400', icon: Package },
            { label: 'Warranty', count: `${summary.warranty_coverage_pct}%`, color: 'text-primary', icon: Shield },
            { label: 'O&M', count: `${summary.om_coverage_pct}%`, color: 'text-blue-600 dark:text-blue-400', icon: Wrench },
            { label: 'Cx Docs', count: summary.with_cx_docs, color: 'text-violet-600 dark:text-violet-400', icon: ClipboardCheck },
            { label: 'VE Removed', count: summary.ve_removed, color: 'text-red-600 dark:text-red-400', icon: XCircle },
          ].map(s => (
            <div key={s.label} className="bg-card border border-border rounded-sm p-3 text-center">
              <s.icon className={`w-4 h-4 mx-auto mb-1 ${s.color}`} />
              <p className={`font-mono text-lg font-bold ${s.color}`}>{s.count}</p>
              <p className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Inventory Tabs */}
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex gap-0.5 overflow-x-auto">
          {[
            { key: 'equipment', label: 'Equipment', icon: Cpu,
              active: 'bg-emerald-100 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-800/40',
              badge: 'bg-emerald-200 dark:bg-emerald-900/50' },
            { key: 'design_chase', label: 'Design Chase', icon: AlertTriangle,
              active: 'bg-amber-100 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 border border-amber-300 dark:border-amber-800/40',
              badge: 'bg-amber-200 dark:bg-amber-900/50' },
            { key: 'landscape', label: 'Landscape', icon: Package,
              active: 'bg-green-100 dark:bg-green-950/30 text-green-700 dark:text-green-400 border border-green-300 dark:border-green-800/40',
              badge: 'bg-green-200 dark:bg-green-900/50' },
            { key: 'site_civil', label: 'Site & Civil', icon: Building2,
              active: 'bg-slate-100 dark:bg-slate-950/30 text-slate-700 dark:text-slate-400 border border-slate-300 dark:border-slate-800/40',
              badge: 'bg-slate-200 dark:bg-slate-900/50' },
            { key: 'architectural_finishes', label: 'Finishes', icon: Package,
              active: 'bg-violet-100 dark:bg-violet-950/30 text-violet-700 dark:text-violet-400 border border-violet-300 dark:border-violet-800/40',
              badge: 'bg-violet-200 dark:bg-violet-900/50' },
            { key: 'structural', label: 'Structural', icon: Package,
              active: 'bg-orange-100 dark:bg-orange-950/30 text-orange-700 dark:text-orange-400 border border-orange-300 dark:border-orange-800/40',
              badge: 'bg-orange-200 dark:bg-orange-900/50' },
            { key: 'chaser', label: 'Chaser', icon: Search,
              active: 'bg-red-100 dark:bg-red-950/30 text-red-700 dark:text-red-400 border border-red-300 dark:border-red-800/40',
              badge: 'bg-red-200 dark:bg-red-900/50' },
          ].map(tab => {
            const count = inventoryCounts[tab.key] || 0;
            const isActive = filter === tab.key;
            const Icon = tab.icon;
            return (
              <button key={tab.key} onClick={() => setFilter(tab.key)}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] font-heading uppercase tracking-wider rounded-sm transition-colors whitespace-nowrap ${
                  isActive ? tab.active : 'text-muted-foreground hover:text-foreground/80 hover:bg-muted/40'
                }`}
                data-testid={`filter-${tab.key}`}>
                <Icon className="w-3 h-3" />
                {tab.label}
                {count > 0 && (
                  <span className={`ml-0.5 font-mono text-[9px] px-1 py-0 rounded-sm ${
                    isActive ? tab.badge : 'bg-muted/60'
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-2">
          <input type="file" ref={regFileRef} accept=".pdf" className="hidden"
            data-testid="build-register-file"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleBuildFromDrawings(f); e.target.value = ''; }} />
          <button onClick={() => regFileRef.current?.click()} disabled={buildingReg}
            data-testid="build-register-btn"
            title="Upload a drawing PDF → register + warranties + PM in one step"
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-heading uppercase tracking-wider rounded-sm transition-colors bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-50">
            {buildingReg ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {buildingReg ? 'Building…' : 'Build Register from Drawings'}
          </button>

          <button onClick={handleExtract} disabled={extracting || classifying}
            data-testid="extract-assets-btn"
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-heading uppercase tracking-wider rounded-sm transition-colors bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-50">
            {extracting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {extracting ? 'Scanning...' : 'Scan & Classify'}
          </button>

          {summary && summary.active > 0 && (
            <>
              {!inventoryCounts.equipment && inventoryCounts.equipment !== 0 && (
                <button onClick={handleClassify} disabled={classifying}
                  data-testid="classify-btn"
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-heading uppercase tracking-wider rounded-sm transition-colors text-amber-600 dark:text-amber-400 border border-amber-300 dark:border-amber-800/40 hover:bg-amber-50 dark:hover:bg-amber-950/30 disabled:opacity-50">
                  {classifying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRightLeft className="w-3.5 h-3.5" />}
                  {classifying ? 'Classifying...' : 'Reclassify'}
                </button>
              )}

              <button onClick={handleFindAll} disabled={findingAll}
                data-testid="find-all-docs-btn"
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-heading uppercase tracking-wider rounded-sm transition-colors bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">
                {findingAll ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                {findingAll ? 'Finding...' : 'Find Warranty & O&M'}
              </button>

              {filter === 'design_chase' && (inventoryCounts.design_chase || 0) > 0 && (
                <>
                  <button onClick={handleExportChaseList}
                    data-testid="export-chase-list-btn"
                    className="flex items-center gap-1.5 px-3 py-2 text-xs font-heading uppercase tracking-wider rounded-sm transition-colors bg-amber-600 hover:bg-amber-500 text-white">
                    <Download className="w-3.5 h-3.5" />
                    Export Chase List
                  </button>

                  <input type="file" ref={chaseFileRef} accept=".xlsx,.xls"
                    onChange={handleImportChaseResponse}
                    className="hidden" data-testid="chase-file-input" />
                  <button onClick={() => chaseFileRef.current?.click()} disabled={importing}
                    data-testid="import-chase-response-btn"
                    className="flex items-center gap-1.5 px-3 py-2 text-xs font-heading uppercase tracking-wider rounded-sm transition-colors text-amber-600 dark:text-amber-400 border border-amber-300 dark:border-amber-800/40 hover:bg-amber-50 dark:hover:bg-amber-950/30 disabled:opacity-50">
                    {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                    {importing ? 'Importing...' : 'Import Response'}
                  </button>
                </>
              )}

              <button onClick={() => handleExport('xlsx')}
                data-testid="export-assets-btn"
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-heading uppercase tracking-wider rounded-sm transition-colors text-cyan-600 dark:text-cyan-400 border border-cyan-300 dark:border-cyan-800/40 hover:bg-cyan-50 dark:hover:bg-cyan-950/30 hover:border-cyan-400 dark:hover:border-cyan-700">
                <Download className="w-3.5 h-3.5" />
                Export
              </button>

              <div className="relative">
                <button onClick={() => setExportOpen(!exportOpen)}
                  data-testid="export-dropdown-btn"
                  className="flex items-center gap-1 px-2 py-2 text-xs text-cyan-600 dark:text-cyan-400 border border-cyan-300 dark:border-cyan-800/40 rounded-sm hover:bg-cyan-50 dark:hover:bg-cyan-950/30 hover:border-cyan-400 dark:hover:border-cyan-700 transition-colors">
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
                {exportOpen && (
                  <div className="absolute right-0 top-full mt-1 bg-card border border-border rounded-sm shadow-lg z-20 min-w-[140px]"
                    data-testid="export-dropdown-menu">
                    {[
                      { fmt: 'xlsx', label: 'Excel (.xlsx)' },
                      { fmt: 'csv', label: 'CSV (.csv)' },
                      { fmt: 'dxf', label: 'AutoCAD (.dxf)' },
                    ].map(opt => (
                      <button key={opt.fmt}
                        data-testid={`export-${opt.fmt}-btn`}
                        onClick={() => { handleExport(opt.fmt); setExportOpen(false); }}
                        className="w-full text-left px-3 py-2 text-xs text-foreground/80 hover:bg-muted/60 hover:text-cyan-400 transition-colors">
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {activeAssets.some(a => a.ai_extracted && !a.verified_by_user) && (
                <button onClick={handleBulkVerify} disabled={verifying}
                  data-testid="bulk-verify-btn"
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-heading uppercase tracking-wider rounded-sm transition-colors text-emerald-600 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-800/40 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 hover:border-emerald-400 dark:hover:border-emerald-700 disabled:opacity-50">
                  {verifying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                  Verify All
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Build-from-Drawings Progress */}
      {buildMsg && (
        <div className="mb-4 p-3 bg-primary/10 border border-primary/30 rounded-sm" data-testid="build-register-progress">
          <div className="flex items-center gap-2 text-xs text-primary">
            {buildingReg && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {buildMsg}
          </div>
        </div>
      )}

      {/* Extraction / Classification Progress */}
      {(extracting || classifying) && (
        <div className="mb-4 p-3 bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-300 dark:border-emerald-900/30 rounded-sm" data-testid="extract-progress">
          <div className="flex items-center gap-2 text-xs text-emerald-700 dark:text-emerald-400">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            {extractProgress?.message || 'Starting...'}
          </div>

          {extractProgress?.status && (
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              {['scanning', 'downloading', 'rendering', 'prescanning', 'analyzing', 'creating', 'deduplicating', 'classifying'].map((phase, i) => {
                const phaseLabels = { scanning: 'Scan', downloading: 'Download', rendering: 'Render', prescanning: 'Pre-Scan', analyzing: 'AI Read', creating: 'Create', deduplicating: 'Dedup', classifying: 'Classify' };
                const phases = ['scanning', 'downloading', 'rendering', 'prescanning', 'analyzing', 'creating', 'deduplicating', 'classifying'];
                const currentPhase = extractProgress.phase || extractProgress.status;
                const currentIdx = phases.indexOf(currentPhase);
                const isActive = i === currentIdx;
                const isDone = currentIdx > -1 && i < currentIdx;
                return (
                  <div key={phase} className={`flex items-center gap-1 text-[9px] font-mono px-1.5 py-0.5 rounded-sm ${
                    isActive ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700' :
                    isDone ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-500 dark:text-emerald-500' :
                    'text-muted-foreground'
                  }`}>
                    {isDone && <CheckCircle2 className="w-2.5 h-2.5" />}
                    {isActive && <Loader2 className="w-2.5 h-2.5 animate-spin" />}
                    {phaseLabels[phase]}
                  </div>
                );
              })}
            </div>
          )}

          {/* Page progress bar — uses pct from backend when available */}
          {(extractProgress?.pct > 0 || extractProgress?.total_pages > 0) && (
            <div className="mt-2">
              <div className="flex justify-between text-[10px] text-muted-foreground mb-1">
                <span>
                  {extractProgress.total_pages > 0
                    ? `Page ${extractProgress.current_page || 0} of ${extractProgress.total_pages}`
                    : `${extractProgress.pct || 0}% complete`
                  }
                </span>
                {extractProgress.total_batches > 0 && <span>Batch {extractProgress.current_batch || 0}/{extractProgress.total_batches}</span>}
              </div>
              <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                  style={{ width: `${extractProgress.pct || (extractProgress.total_pages ? Math.round(((extractProgress.current_page || 0) / extractProgress.total_pages) * 100) : 0)}%` }} />
              </div>
            </div>
          )}

          {/* Generic progress bar for non-page phases */}
          {!extractProgress?.total_pages && extractProgress?.total > 0 && (
            <div className="mt-2 h-1.5 bg-secondary rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                style={{ width: `${Math.round((extractProgress.current / extractProgress.total) * 100)}%` }} />
            </div>
          )}

          {/* Items found counter */}
          {extractProgress?.items_found > 0 && (
            <p className="mt-1.5 text-[10px] text-emerald-600 dark:text-emerald-400 font-mono">
              {extractProgress.items_found} equipment items found so far
            </p>
          )}

          {extractProgress?.status === 'classifying' && (
            <p className="mt-1.5 text-[10px] text-muted-foreground">Classifying items into inventory categories and merging duplicates...</p>
          )}
        </div>
      )}

      {/* Doc Finder Progress */}
      {findingAll && docFinderProgress && (
        <div className="mb-4 p-3 bg-amber-50 dark:bg-orange-950/20 border border-primary/30 rounded-sm" data-testid="finder-progress">
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-2 text-primary">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Finding docs for: {docFinderProgress.equipment}
            </div>
            <span className="font-mono text-muted-foreground">
              {docFinderProgress.current}/{docFinderProgress.total}
            </span>
          </div>
          <div className="mt-2 h-1.5 bg-secondary rounded-full overflow-hidden">
            <div className="h-full bg-primary rounded-full transition-all duration-500"
              style={{ width: `${Math.round((docFinderProgress.current / docFinderProgress.total) * 100)}%` }} />
          </div>
        </div>
      )}

      {/* Import Result Banner */}
      {importResult && (
        <div className={`mb-4 p-3 rounded-sm border ${
          importResult.status === 'error'
            ? 'bg-red-50 dark:bg-red-950/20 border-red-300 dark:border-red-800/30'
            : 'bg-emerald-50 dark:bg-emerald-950/20 border-emerald-300 dark:border-emerald-800/30'
        }`} data-testid="import-result-banner">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs">
              {importResult.status === 'error'
                ? <XCircle className="w-4 h-4 text-red-500" />
                : <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
              <span className={importResult.status === 'error' ? 'text-red-700 dark:text-red-400' : 'text-emerald-700 dark:text-emerald-400'}>
                {importResult.message}
              </span>
            </div>
            <button onClick={() => setImportResult(null)} className="text-xs text-muted-foreground hover:text-foreground">
              Dismiss
            </button>
          </div>

          {importResult.promoted_to_equipment > 0 && (
            <p className="mt-1.5 text-[10px] text-emerald-600 dark:text-emerald-400">
              {importResult.promoted_to_equipment} items now fully specified and promoted to Equipment tab
            </p>
          )}

          {importResult.errors?.length > 0 && (
            <div className="mt-2 space-y-0.5 max-h-24 overflow-y-auto">
              {importResult.errors.map((err, i) => (
                <p key={i} className="text-[10px] text-red-600 dark:text-red-400">{err}</p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Asset List */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : assets.length === 0 ? (
        <div className="text-center py-12 bg-card border border-dashed border-border rounded-sm">
          <Package className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">
            {filter === 'equipment' ? 'No fully-specified equipment yet — items need make, model, and location' :
             filter === 'design_chase' ? 'No design chase items — all equipment is fully specified' :
             filter === 'chaser' ? 'No unclassified items — everything has been categorized' :
             `No ${filter.replace('_', ' ')} items found`}
          </p>
          {summary?.total === 0 && (
            <p className="text-xs text-muted-foreground mt-1">
              Click "Scan & Classify" to extract and categorize items from your project documents
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {assets.map(a => (
            <AssetCard
              key={a.id}
              asset={a}
              projectId={projectId}
              onRefresh={fetchData}
              currentTab={filter}
              onInlineEdit={handleInlineEdit}
              onReassign={handleReassign}
            />
          ))}
        </div>
      )}

      {showBuildReview && (
        <HandoverReviewModal
          projectId={projectId}
          mode="post-build"
          onClose={() => setShowBuildReview(false)}
        />
      )}
    </div>
  );
}
